import type { Client } from '@/api/generated/client';
import type { CreateClientConfig } from '@/api/generated/client.gen';
import { ApiError } from '@/lib/errors';

type FetchClient = {
  baseUrl: string;
  get: <T>(
    path: string,
    options?: { params?: Record<string, unknown>; signal?: AbortSignal }
  ) => Promise<T>;
  post: <T>(path: string, options?: { body?: unknown; signal?: AbortSignal }) => Promise<T>;
};

function createApiClient(providedBaseUrl?: string): FetchClient {
  const envBaseUrl = import.meta.env.VITE_API_URL;
  const mocksEnabled = import.meta.env.VITE_ENABLE_MOCKS === 'true';

  const baseUrl = providedBaseUrl ?? envBaseUrl ?? (mocksEnabled ? '' : 'http://localhost:3000');

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${baseUrl}${path}`;
    const response = await fetch(url, init);

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  return {
    baseUrl,
    get: async <T>(
      path: string,
      options?: { params?: Record<string, unknown>; signal?: AbortSignal }
    ): Promise<T> => {
      let url = path;

      if (options?.params) {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(options.params)) {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
          }
        }
        url = `${path}?${searchParams.toString()}`;
      }

      return request<T>(url, { method: 'GET', signal: options?.signal });
    },
    post: async <T>(
      path: string,
      options?: { body?: unknown; signal?: AbortSignal }
    ): Promise<T> => {
      return request<T>(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: options?.body ? JSON.stringify(options.body) : undefined,
        signal: options?.signal,
      });
    },
  };
}

const api = createApiClient();

/**
 * Extracts a message from an error response body.
 * Handles common API error formats like { message: "..." } or { detail: "..." }
 */
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('detail' in error && typeof error.detail === 'string') {
      return error.detail;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
  }

  return 'Unknown error';
}

// This is used to create the client config for the generated api client
export const createClientConfig: CreateClientConfig = (config) => {
  return {
    ...config,
    baseUrl: api.baseUrl,
  };
};

/**
 * Sets up error interceptors on the generated client to create ApiError instances
 * with full request/response context for logging.
 * This should be called after the client is created.
 */
export function setupClientErrorHandling(client: Client) {
  client.interceptors.error.use((error, response, request, options) => {
    const path = options?.url || request?.url || 'unknown';
    const errorMessage = extractErrorMessage(error);

    return new ApiError(`API request failed: ${path} - ${errorMessage}`, {
      cause: error,
      status: response?.status,
      statusText: response?.statusText,
      url: request?.url,
      responseData: error, // The parsed JSON error body from hey-api
    });
  });
}

export { createApiClient };
